import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { API } from '../../App';

/**
 * Public inquiry form used from the landing page for both the "join beta" and
 * "discuss a pilot" flows. Posts to /api/public/beta-inquiry which emails the
 * founder AND persists into `beta_inquiries` for the admin panel.
 */
const BetaInquiryDialog = ({ open, onOpenChange, kind = 'beta' }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [org, setOrg] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isPilot = kind === 'pilot';
  const title = isPilot ? 'Discuss a pilot' : 'Join the free beta';
  const description = isPilot
    ? 'Tell us a bit about your organization and what you have in mind — we usually reply within a day.'
    : 'Give us your name and email and we\'ll get back to you with next steps for the free beta.';

  const reset = () => { setName(''); setEmail(''); setOrg(''); setNote(''); };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error('Please add your name and email so we can reach you.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/beta-inquiry`, {
        name: name.trim(), email: email.trim(), org: org.trim(), note: note.trim(), kind,
      });
      toast.success(isPilot ? "Thanks! We'll be in touch about a pilot soon." : "Thanks! We'll be in touch about beta access soon.");
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't submit — please try again in a minute.");
    }
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="surface-card max-w-md" data-testid={`beta-inquiry-dialog-${kind}`}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Input placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} className="rounded-xl" data-testid="beta-name-input" required />
          <Input type="email" placeholder="Your email" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-xl" data-testid="beta-email-input" required />
          {isPilot && (
            <Input placeholder="Organization (optional)" value={org} onChange={(e) => setOrg(e.target.value)} className="rounded-xl" data-testid="beta-org-input" />
          )}
          <Textarea
            placeholder={isPilot ? 'Tell us about your org and what a pilot might look like…' : "Anything you'd like us to know (optional)"}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="rounded-xl resize-none"
            rows={3}
            data-testid="beta-note-input"
          />
          <Button type="submit" className="w-full btn-primary" disabled={submitting} data-testid="beta-submit-btn">
            {submitting ? 'Sending…' : (isPilot ? 'Request a pilot conversation' : 'Request beta access')}
          </Button>
          <p className="text-[11px] text-muted-foreground text-center">
            Prefer email? Write to <a href="mailto:info@circleupsquad.com" className="underline underline-offset-2">info@circleupsquad.com</a>
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BetaInquiryDialog;
